import React, { useState } from "react";
import { FaPlus, FaTrashAlt, FaEdit } from "react-icons/fa";

const AdditionalLocationsTab = ({ locations, onSave }) => {
  const [locationList, setLocationList] = useState(locations || []);
  const [currentLocation, setCurrentLocation] = useState({});
  const [editIndex, setEditIndex] = useState(null);

  const handleAdd = () => {
    const {
      type,
      addressLine1,
      city,
      state,
      pincode,
      contactPersonName,
      contactPhone,
    } = currentLocation;

    if (
      !type ||
      !addressLine1 ||
      !city ||
      !state ||
      !pincode ||
      !contactPersonName ||
      !contactPhone
    ) {
      alert("Please fill in all required fields.");
      return;
    }

    if (editIndex !== null) {
      setLocationList((prev) =>
        prev.map((loc, idx) => (idx === editIndex ? currentLocation : loc))
      );
    } else {
      setLocationList((prev) => [...prev, currentLocation]);
    }

    setCurrentLocation({});
    setEditIndex(null);
  };

  const handleEdit = (index) => {
    setCurrentLocation(locationList[index]);
    setEditIndex(index);
  };

  const handleDelete = (index) => {
    setLocationList((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCurrentLocation((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    onSave(locationList);
  };

  return (
    <div>
      <h3 className="text-lg font-bold mb-4">Additional Locations</h3>
      <div className="grid grid-cols-2 gap-4">
        {/* Location Type */}
        <div>
          <label className="block mb-1 font-medium">Location Type*</label>
          <input
            type="text"
            name="type"
            placeholder="e.g., Warehouse"
            value={currentLocation?.type || ""}
            onChange={handleChange}
            className="border p-2 rounded w-full"
          />
        </div>

        {/* Address Line 1 */}
        <div>
          <label className="block mb-1 font-medium">Address Line 1*</label>
          <input
            type="text"
            name="addressLine1"
            placeholder="Address Line 1"
            value={currentLocation?.addressLine1 || ""}
            onChange={handleChange}
            className="border p-2 rounded w-full"
          />
        </div>

        {/* Address Line 2 */}
        <div>
          <label className="block mb-1 font-medium">Address Line 2</label>
          <input
            type="text"
            name="addressLine2"
            placeholder="Address Line 2"
            value={currentLocation?.addressLine2 || ""}
            onChange={handleChange}
            className="border p-2 rounded w-full"
          />
        </div>

        {/* City */}
        <div>
          <label className="block mb-1 font-medium">City*</label>
          <input
            type="text"
            name="city"
            placeholder="City"
            value={currentLocation?.city || ""}
            onChange={handleChange}
            className="border p-2 rounded w-full"
          />
        </div>

        {/* State */}
        <div>
          <label className="block mb-1 font-medium">State*</label>
          <input
            type="text"
            name="state"
            placeholder="State"
            value={currentLocation?.state || ""}
            onChange={handleChange}
            className="border p-2 rounded w-full"
          />
        </div>

        {/* Pincode */}
        <div>
          <label className="block mb-1 font-medium">Pincode*</label>
          <input
            type="text"
            name="pincode"
            placeholder="Pincode"
            value={currentLocation?.pincode || ""}
            onChange={handleChange}
            className="border p-2 rounded w-full"
          />
        </div>

        {/* Contact Person Name */}
        <div>
          <label className="block mb-1 font-medium">Contact Person Name*</label>
          <input
            type="text"
            name="contactPersonName"
            placeholder="Contact Person Name"
            value={currentLocation?.contactPersonName || ""}
            onChange={handleChange}
            className="border p-2 rounded w-full"
          />
        </div>

        {/* Contact Phone */}
        <div>
          <label className="block mb-1 font-medium">Contact Phone*</label>
          <input
            type="text"
            name="contactPhone"
            placeholder="Contact Phone"
            value={currentLocation?.contactPhone || ""}
            onChange={handleChange}
            className="border p-2 rounded w-full"
          />
        </div>

        {/* Contact Email */}
        <div>
          <label className="block mb-1 font-medium">Contact Email</label>
          <input
            type="email"
            name="contactEmail"
            placeholder="Contact Email"
            value={currentLocation?.contactEmail || ""}
            onChange={handleChange}
            className="border p-2 rounded w-full"
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 mt-4">
        <button
          onClick={handleAdd}
          className="px-4 py-2 bg-[#c7eac9] rounded text-[#191919] hover:bg-[#ebf8ec]"
        >
          {editIndex !== null ? "Update Location" : "Add Location"}
        </button>
        {editIndex !== null && (
          <button
            onClick={() => {
              setCurrentLocation({});
              setEditIndex(null);
            }}
            className="px-4 py-2 bg-gray-300 rounded text-[#191919] hover:bg-gray-400"
          >
            Cancel
          </button>
        )}
      </div>

      {/* Locations List */}
      <table className="w-full border-collapse mt-4">
        <thead>
          <tr className="bg-[#efefef] border-b border-[#d8d8d8] text-sm">
            <th className="p-3 text-left font-semibold">Type</th>
            <th className="p-3 text-left font-semibold">Address</th>
            <th className="p-3 text-left font-semibold">City</th>
            <th className="p-3 text-left font-semibold">Contact Person</th>
            <th className="p-3 text-left font-semibold">Actions</th>
          </tr>
        </thead>
        <tbody>
          {locationList.map((loc, index) => (
            <tr key={index} className="border-b border-[#d8d8d8] text-sm">
              <td className="p-3">{loc.type}</td>
              <td className="p-3">
                {loc.addressLine1}
                {loc.addressLine2 && `, ${loc.addressLine2}`}
              </td>
              <td className="p-3">{loc.city}</td>
              <td className="p-3">{loc.contactPersonName}</td>
              <td className="p-3 flex gap-2">
                <button
                  onClick={() => handleEdit(index)}
                  className="text-blue-600 hover:underline flex items-center gap-1"
                >
                  <FaEdit /> Edit
                </button>
                <button
                  onClick={() => handleDelete(index)}
                  className="text-red-600 hover:underline flex items-center gap-1"
                >
                  <FaTrashAlt /> Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default AdditionalLocationsTab;
